<script setup lang="ts">
import { usePasskeys } from '@/composables/usePasskeys'
import { computed, ref } from 'vue'

const {
  passkeys, isLoading, isError,
  register, registerLoading, registerError,
  deletePasskey, deletingId, deleteLoading,
} = usePasskeys()

const addSuccess = ref(false)
const newKeyName = ref('')
const showNameInput = ref(false)

const addErrorMsg = computed(() => {
  const e = registerError.value as any
  if (!e) return ''
  if (e?.name === 'NotAllowedError') return 'Cancelado por el usuario'
  return e?.response?.data?.message ?? 'No se pudo registrar'
})

const startAdd = () => {
  newKeyName.value = ''
  showNameInput.value = true
}

const confirmAdd = async () => {
  if (!newKeyName.value.trim()) return
  addSuccess.value = false
  try {
    await register(newKeyName.value.trim())
    addSuccess.value = true
    showNameInput.value = false
    setTimeout(() => { addSuccess.value = false }, 2500)
  } catch {
    // error exposed via registerError
  }
}

const handleDelete = (id: number) => deletePasskey(id)

const formatDate = (iso: string | null) => {
  if (!iso) return 'Nunca'
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}
</script>

<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-sm font-semibold text-gray-900">Passkeys</h3>
        <p class="text-xs text-gray-400 mt-0.5">Acceso con huella, Face ID o llave de seguridad</p>
      </div>
      <button
        v-if="!showNameInput"
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors"
        :class="addSuccess
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-white border-gray-200 text-gray-700 hover:border-blue-600 hover:text-blue-600'"
        @click="startAdd"
      >
        <span class="material-symbols-outlined text-[14px] leading-none">{{ addSuccess ? 'check' : 'add' }}</span>
        {{ addSuccess ? 'Agregada' : 'Agregar passkey' }}
      </button>
    </div>

    <!-- Name input for new passkey -->
    <Transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="showNameInput" class="rounded-xl border border-blue-200 bg-blue-50/50 px-4 py-3 space-y-3">
        <p class="text-xs text-gray-600 font-medium">Pon un nombre a esta passkey (ej: "MacBook Pro", "iPhone 15")</p>
        <div class="flex items-center gap-2">
          <input
            v-model="newKeyName"
            type="text"
            placeholder="Mi dispositivo"
            maxlength="50"
            class="flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all"
            @keydown.enter="confirmAdd"
            @keydown.escape="showNameInput = false"
          />
          <button
            type="button"
            :disabled="registerLoading || !newKeyName.trim()"
            @click="confirmAdd"
            class="px-3 py-2 rounded-lg text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
          >
            <svg v-if="registerLoading" class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ registerLoading ? 'Verificando...' : 'Continuar' }}
          </button>
          <button
            type="button"
            class="px-2 py-2 rounded-lg text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            @click="showNameInput = false"
          >
            <span class="material-symbols-outlined text-[16px] leading-none">close</span>
          </button>
        </div>
        <p v-if="addErrorMsg" class="text-xs text-red-500">{{ addErrorMsg }}</p>
      </div>
    </Transition>

    <!-- Passkey list -->
    <div v-if="isLoading" class="space-y-2">
      <div v-for="i in 2" :key="i" class="h-14 rounded-xl bg-gray-100 animate-pulse" />
    </div>

    <div v-else-if="isError" class="text-xs text-red-500 py-2">No se pudieron cargar las passkeys</div>

    <div v-else-if="!passkeys?.length" class="rounded-xl border border-dashed border-gray-200 py-8 flex flex-col items-center gap-2 text-gray-400">
      <svg viewBox="0 0 24 24" class="w-8 h-8" fill="none" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33" />
      </svg>
      <p class="text-xs">Sin passkeys registradas</p>
    </div>

    <div v-else class="space-y-2">
      <div
        v-for="key in passkeys"
        :key="key.id"
        class="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-white hover:border-gray-200 transition-colors"
      >
        <div class="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 24 24" class="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M7.864 4.243A7.5 7.5 0 0119.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 004.5 10.5a7.464 7.464 0 01-1.15 3.993m1.989 3.559A11.209 11.209 0 008.25 10.5a3.75 3.75 0 117.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 01-3.6 9.75m6.633-4.596a18.666 18.666 0 01-2.485 5.33" />
          </svg>
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-gray-800 truncate">{{ key.name }}</p>
          <p class="text-[11px] text-gray-400 mt-0.5">
            Creada {{ formatDate(key.created_at) }}
            <span v-if="key.last_used_at"> · Último uso {{ formatDate(key.last_used_at) }}</span>
          </p>
        </div>
        <button
          type="button"
          :disabled="deleteLoading && deletingId === key.id"
          @click="handleDelete(key.id)"
          class="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
        >
          <svg v-if="deleteLoading && deletingId === key.id" class="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span v-else class="material-symbols-outlined text-[16px] leading-none">delete</span>
        </button>
      </div>
    </div>
  </div>
</template>
